import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { getPageLimit, showError, showSuccess, updatePageLimit } from 'utils';
import { createSupplierInventoryMapping, deleteSupplierInventoryMapping, fetchAllSupplierInventoryMappingsForAdmin } from 'services/api/suppliers';
import { Loading } from 'components/loading/Loading';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import ActionButton from 'components/ActionButton';
import styles from './index.module.scss';
import AddInventoryItemModal from 'components/AddInventoryItemModal';
import Currency from 'components/Currency/Currency';
import InventoryItemId from 'components/InventoryItemId';


const filters: { [p: string]: any; page: number } = {
  page: 0,
};

const InventoryItems = props => {
  const { supplierId, addInventoryItemModalOpen, setAddInventoryItemModalOpen } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Record<string, any>[]>([]);

  const fetchInventoryItemsForSupplierFn = async () => {
    setLoading(true);
    try {
      const response = await fetchAllSupplierInventoryMappingsForAdmin(supplierId);
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItemsForSupplierFn();
  }, []);

  const deleteInventoryItem = async (mappingId: string) => {
    try {
      await deleteSupplierInventoryMapping(mappingId);
      showSuccess('Item removed successfully.');
      await fetchInventoryItemsForSupplierFn();
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() =>  [
    {
      name: 'ItemID',
      selector: 'inventoryItem.objectId',
      sortable: false,
      width: '180px',
      // eslint-disable-next-line react/display-name
      cell: row => row.inventoryItem ? (
        <InventoryItemId itemDetails={row.inventoryItem} />
      ): '-',
    },
    {
      name: 'Name',
      selector: 'inventoryItem.name',
      sortable: true,
      width: '350px',
      format: row => <div className="text-wrap">{row.inventoryItem?.name || '-'}</div>,
    },
    {
      name: 'Inventory Name For Supplier',
      selector: 'inventoryNameForSupplier',
      sortable: true,
      width: '350px',
      format: row => <div className="text-wrap">{row.inventoryNameForSupplier || '-'}</div>,
    },
    {
      name: 'Cost',
      selector: 'cost',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: (row) => (<Currency amount={parseFloat(row.cost?.toFixed(2))} default={'-'} />),
    },
    {
      name: 'Quantity',
      selector: 'inventoryItem.stock',
      sortable: false,
      format: row => row.inventoryItem?.stock || '-',
    },
    {
      name: 'Unfulfilled Quantity',
      selector: 'inventoryItem.unfulfilledStock',
      sortable: false,
      format: row => row.inventoryItem?.unfulfilledStock || '-',
    },
    {
      name: 'Producer Unit Mapping',
      selector: 'inventoryItem.unit.description',
      sortable: true,
      format: row => row.inventoryItem?.unit?.description || '-',
    },
    {
      name: 'Supplier Unit Mapping',
      selector: 'supplierUnit',
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      width: '100px',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => deleteInventoryItem(row.objectId)}
          />
        </div>
      ),
    },
  ], []);
  
  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchInventoryItemsForSupplierFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const toggleModal = () => setAddInventoryItemModalOpen(!addInventoryItemModalOpen);
  
  const addInventoryItem = async (params) => {
    const { item, supplierUnit, cost, inventoryNameForSupplier } = params;
    try {
      await createSupplierInventoryMapping({ supplierId,
        inventoryItemId: item.id,
        supplierUnit,
        cost,
        inventoryNameForSupplier,
      });
      showSuccess('Item added successfully');
      fetchInventoryItemsForSupplierFn();
      toggleModal();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['inventory-items-container']}>
      {addInventoryItemModalOpen && (
        <AddInventoryItemModal
          supplierId={supplierId}
          onSubmit={addInventoryItem}
          toggleModal={toggleModal}
          heading="Add A New Inventory Item"
          showUnitMappingInput
          showInventoryNameForSupplierInput
        />
      )}
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Inventory Items</div>
        <ActionButton
          active={true}
          isLoading={false}
          onClick={toggleModal}
          text={<>Inventory Item <span className={'ml-3'}>+</span></>}
        />
      </div>
      {loading && <Loading type={'mini'} />}
      <DataTable
        columns={columns}
        noHeader={true}
        data={data}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField="objectId"
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.length}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  );
};

export default InventoryItems;